import React, { useMemo } from 'react';
import { DashboardDataRaw, DateRange } from '../../types/Dashboard';
import { activesAtDate } from './dashboardHelpers';
import { Panel, Badge } from '../ui';
import { planColor, formatNumber } from './dashboardUi';

interface Props {
  data: DashboardDataRaw;
  range: DateRange;
}

const th = 'pb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400';

const TotalSuscriptoresTable: React.FC<Props> = ({ data, range }) => {
  const { rows, total, totalPremium, totalFree } = useMemo(() => {
    const activesEnd = activesAtDate(data.premium, range.to);

    const premiumRows = Object.entries(activesEnd).map(([plan, cantidad]) => ({
      plan,
      cantidad,
      tipo: 'Premium' as const,
    }));

    const freeRows = data.freeActivos.map((f) => ({
      plan: f.plan,
      cantidad: f.cantidad,
      tipo: 'Free' as const,
    }));

    premiumRows.sort((a, b) => b.cantidad - a.cantidad);
    freeRows.sort((a, b) => b.cantidad - a.cantidad);

    const rows = [...premiumRows, ...freeRows];
    const total = rows.reduce((s, r) => s + r.cantidad, 0);
    const totalPremium = premiumRows.reduce((s, r) => s + r.cantidad, 0);
    const totalFree = freeRows.reduce((s, r) => s + r.cantidad, 0);
    return { rows, total, totalPremium, totalFree };
  }, [data, range]);

  return (
    <Panel
      title="Suscriptores por plan"
      subtitle="Premium al cierre del rango filtrado. Los planes Free son la foto de hoy: el filtro de fechas no los afecta."
    >
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200 text-left">
            <th className={`${th} pe-4`}>Plan</th>
            <th className={`${th} pe-4`}>Tipo</th>
            <th className={`${th} text-right`}>Activos</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={`${row.tipo}-${row.plan}`}
              className="border-b border-slate-100 transition-colors duration-100 ease-out last:border-0 hover:bg-slate-50/70"
            >
              <td className="py-2 pe-4">
                <span className="flex items-center gap-2">
                  <span
                    className="h-2.5 w-2.5 shrink-0 rounded-full"
                    style={{
                      backgroundColor: row.tipo === 'Premium' ? planColor(row.plan) : '#cbd5e1',
                    }}
                  />
                  <span className="font-medium text-gappex-secondary">{row.plan}</span>
                </span>
              </td>
              <td className="py-2 pe-4">
                <Badge
                  tone={row.tipo === 'Premium' ? 'info' : 'neutral'}
                  title={
                    row.tipo === 'Premium'
                      ? 'Plan de pago, medido al cierre del rango'
                      : 'Plan sin costo, foto del día de hoy'
                  }
                >
                  {row.tipo === 'Free' ? 'Free · hoy' : 'Premium'}
                </Badge>
              </td>
              <td className="py-2 text-right font-medium tabular-nums text-gappex-secondary">
                {formatNumber(row.cantidad)}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t-2 border-slate-200 font-semibold text-gappex-secondary">
            <td className="pt-3 pe-4" colSpan={2}>
              Total general
            </td>
            <td className="pt-3 text-right tabular-nums">{formatNumber(total)}</td>
          </tr>
          <tr>
            <td className="pt-1 text-[11px] tabular-nums text-slate-400" colSpan={3}>
              {formatNumber(totalPremium)} premium · {formatNumber(totalFree)} free
            </td>
          </tr>
        </tfoot>
      </table>
    </Panel>
  );
};

export default TotalSuscriptoresTable;
